// Mahallu profile, brand and app settings (pilot slice 11, ADR-0033, ADR-0037).
import type { Role } from './auth';
import type { TemplateCode } from './messaging';

/** Who may change the profile, mark, colour and settings. Reading the profile is open to every role. */
export const PROFILE_ROLES: readonly Role[] = ['MAHALLU_ADMIN', 'PRESIDENT', 'SECRETARY'];
/** Who may read the audit log screen. */
export const AUDIT_ROLES: readonly Role[] = ['MAHALLU_ADMIN', 'PRESIDENT', 'SECRETARY'];

/** The product brand (ADR-0033). The mahallu's own colour never replaces it. */
export const BRAND = { teal: '#1F7A6D', gold: '#E0A526', ink: '#0A0A0A', name: 'Rabita One', descriptor: 'Mahallu Cloud by OptiFi', tagline: 'Every mahallu, connected.' } as const;

export const MARK_LIMITS = { bytes: 200 * 1024, types: ['image/png', 'image/jpeg'] as const } as const;

/** Messages a mahallu may switch off, and whether SMS may stand in for WhatsApp. Safety messages are locked. */
export const SWITCHABLE: readonly TemplateCode[] = ['RECEIPT_ISSUED', 'ANNOUNCEMENT'];
export const LOCKED_ON: readonly TemplateCode[] = ['SIGN_IN_CODE', 'JANAZA_ALERT'];
/** Messages whose free text cannot go by DLT SMS: their SMS switch is always off. */
export const WHATSAPP_ONLY: readonly TemplateCode[] = ['ANNOUNCEMENT'];

export interface MessageSwitch {
  code: TemplateCode;
  on: boolean;
  sms: boolean;
  locked: boolean;
  smsPossible: boolean;
}

export interface AppSettings {
  messageLang: 'ML' | 'EN';
  receiptNotifyDefault: boolean;
  switches: MessageSwitch[];
  version: number;
}

export interface AppSettingsPatch {
  messageLang?: 'ML' | 'EN';
  receiptNotifyDefault?: boolean;
  switches?: { code: TemplateCode; on: boolean; sms: boolean }[];
  version: number | null;
}

export interface MahalluProfile {
  nameEn: string;
  nameMl: string | null;
  addressEn: string | null;
  addressMl: string | null;
  district: string | null;
  pinCode: string | null;
  phone: string | null;
  email: string | null;
  waqfRegNo: string | null;
  reg12a: string | null;
  reg80g: string | null;
  colour: string | null;
  /** Present when a mark is uploaded; changes with the image so browsers fetch the new one. */
  markHash: string | null;
  version: number | null;
}

export type ProfilePatch = Partial<Omit<MahalluProfile, 'markHash' | 'version'>> & { version: number | null };

/** WCAG relative luminance contrast of a colour against white; the mahallu colour needs 4.5 : 1 for text. */
export function contrastOnWhite(hex: string): number {
  const c = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255).map((x) => (x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4));
  const l = 0.2126 * c[0]! + 0.7152 * c[1]! + 0.0722 * c[2]!;
  return 1.05 / (l + 0.05);
}

export interface AuditRow {
  id: string;
  at: string;
  actor: string | null;
  actorRole: string | null;
  action: string;
  subjectType: string;
  subjectId: string | null;
  detail: Record<string, unknown>;
}

export interface AuditList {
  rows: AuditRow[];
  total: number;
  page: number;
  size: number;
  /** Action names present, for the filter. */
  actions: string[];
}

export const AUDIT_FILTERS = ['action', 'from', 'to', 'q', 'page', 'size'] as const;
